import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newId, type ExpenseCategoryRecord } from '@dairy/shared';
import { useSession } from '@/auth/useSession';
import { toast } from '@/components/toast';
import { Badge, Button, Card, CardTitle, Checkbox, Dialog, Field, Input, PageHeader, Select } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { save } from '@/db/repo';
import { categoryName, useCategories } from '@/features/expenses/CategorySelect';

/** EX-2 categories (owner): add, rename, deactivate. Seeded categories and categories in use cannot be deleted. */
export function CategoriesPage() {
  const { t } = useTranslation();
  const locale = useSession().profile?.locale ?? 'am';
  const cats = useCategories();
  const groups = cats.filter((c) => !c.parentId).sort((a, b) => a.sortOrder - b.sortOrder);
  const [editing, setEditing] = useState<ExpenseCategoryRecord | { parentId: string } | null>(null);
  return (
    <div className="mx-auto max-w-lg space-y-4">
      <PageHeader title={t('settings.categories')} />
      {groups.map((g) => (
        <Card key={g.id}>
          <div className="mb-2 flex items-center justify-between">
            <CardTitle className="mb-0">
              {categoryName(g, locale)} {g.costType === 'CAPITAL' ? <Badge tone="purple">{t('categories.capital')}</Badge> : null}
            </CardTitle>
            <Button size="sm" variant="secondary" onClick={() => setEditing({ parentId: g.id })}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <ul className="divide-y divide-stone-100">
            {cats
              .filter((c) => c.parentId === g.id)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((c) => (
                <li key={c.id}>
                  <button className="flex min-h-12 w-full items-center gap-2 text-left" onClick={() => setEditing(c)}>
                    <span className="flex-1">{categoryName(c, locale)}</span>
                    {c.isFeed ? <Badge tone="green">{t('categories.feed')}</Badge> : null}
                    {!c.active ? <Badge>{t('common.inactive')}</Badge> : null}
                  </button>
                </li>
              ))}
          </ul>
        </Card>
      ))}
      {editing ? <CategoryDialog item={editing} groups={groups} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function CategoryDialog({ item, groups, onClose }: { item: ExpenseCategoryRecord | { parentId: string }; groups: ExpenseCategoryRecord[]; onClose: () => void }) {
  const { t } = useTranslation();
  const existing = 'id' in item ? item : null;
  const [parentId, setParentId] = useState(item.parentId ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [nameAm, setNameAm] = useState(existing?.nameAm ?? '');
  const [isFeed, setIsFeed] = useState(existing?.isFeed ?? false);
  const [active, setActive] = useState(existing?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const parent = groups.find((g) => g.id === parentId);
  const submit = async () => {
    if (!name.trim() || !nameAm.trim()) return setError(t('categories.errors.names'));
    try {
      await save('expenseCategory', {
        ...(existing ?? {}),
        id: existing?.id ?? newId(),
        name: name.trim(),
        nameAm: nameAm.trim(),
        parentId: existing && !existing.parentId ? null : parentId,
        costType: parent?.costType ?? existing?.costType ?? 'OPERATING',
        isFeed,
        systemKey: existing?.systemKey ?? null,
        active,
        sortOrder: existing?.sortOrder ?? 900,
      });
      toast(t('common.saved'));
      onClose();
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={existing ? t('categories.edit') : t('categories.add')} footer={<Button onClick={() => void submit()}>{t('common.save')}</Button>}>
      <div className="space-y-3">
        {!existing || existing.parentId ? (
          <Field label={t('categories.group')}>
            <Select value={parentId} onChange={(e) => setParentId(e.target.value)} disabled={!!existing?.systemKey}>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label={t('categories.nameEn')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </Field>
        <Field label={t('categories.nameAm')}>
          <Input value={nameAm} onChange={(e) => setNameAm(e.target.value)} maxLength={60} />
        </Field>
        <Checkbox label={t('categories.isFeed')} checked={isFeed} onChange={(e) => setIsFeed(e.target.checked)} />
        <Checkbox label={t('common.active')} checked={active} onChange={(e) => setActive(e.target.checked)} />
        {existing?.systemKey ? <p className="text-sm text-stone-600">{t('categories.seededHint')}</p> : null}
        {error ? <p className="text-red-700">{error}</p> : null}
      </div>
    </Dialog>
  );
}
